import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Menu, Settings as SettingsIcon, TrendingUp } from 'lucide-react';
import { useSearchCoins } from '../hooks/useCoins';
import { useSettings } from '../hooks/useSettings';

// Force HMR update on file change
if (import.meta.hot) {
  import.meta.hot.accept();
}

export default function Header() {
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const { results, loading, search } = useSearchCoins();
  const { settings } = useSettings();
  const navigate = useNavigate();

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (query.length >= 2) {
      search(query);
      setShowResults(true);
    } else {
      setShowResults(false);
    }
  };

  const handleSelectCoin = (coinId: string) => {
    navigate(`/coin/${coinId}`);
    setSearchQuery('');
    setShowResults(false);
  };

  const currencySymbol = {
    usd: '$',
    eur: '€',
    gbp: '£',
    jpy: '¥',
    mxn: '$',
    php: '₱',
  }[settings.currency];

  return (
    <header className="sticky top-0 z-50 bg-white/80 dark:bg-black/80 backdrop-blur-lg border-b border-gray-200 dark:border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="bg-black dark:bg-white p-2 rounded-lg">
                <TrendingUp className="h-5 w-5 text-white dark:text-black" />
              </div>
              <span className="text-xl font-bold text-black dark:text-white">
                BlockPeek
              </span>
            </Link>
          </div>

          <div className="flex-1 max-w-xl mx-8 hidden md:block">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearch}
                placeholder="Search coins..."
                className="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-gray-800 border-0 rounded-lg text-sm focus:ring-2 focus:ring-black dark:focus:ring-white focus:outline-none transition-all"
              />
              {showResults && results.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-black rounded-lg shadow-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                  {loading ? (
                    <div className="p-4 text-center text-gray-500">Searching...</div>
                  ) : (
                    results.map((coin) => (
                      <button
                        key={coin.id}
                        onClick={() => handleSelectCoin(coin.id)}
                        className="w-full flex items-center gap-3 p-3 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                      >
                        <img src={coin.image} alt={coin.name} className="h-6 w-6 rounded-full" />
                        <span className="font-medium text-black dark:text-white">{coin.name}</span>
                        <span className="text-gray-500 text-sm uppercase">{coin.symbol}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Currency:</span>
              <span className="text-sm font-bold text-black dark:text-white uppercase">
                {currencySymbol}
              </span>
            </div>
            <Link
              to="/settings"
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              title="Settings"
            >
              <SettingsIcon className="h-5 w-5 text-slate-600 dark:text-slate-400" />
            </Link>
            <button className="md:hidden p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
              <Menu className="h-5 w-5 text-slate-600 dark:text-slate-400" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}